import { cn } from '@/lib/utils'
import type { Belt } from '@/types/account'

interface BeltVisualizerProps {
  belt: Belt | null
  rank: string | null
  className?: string
}

const beltColors: Record<Belt, string> = {
  WHITE: '#FFFFFF',
  BLUE: '#0033A0',
  PURPLE: '#6B3FA0',
  BROWN: '#8B4513',
  BLACK: '#000000',
}

const beltBorderColors: Record<Belt, string> = {
  WHITE: '#E5E5E5',
  BLUE: '#002080',
  PURPLE: '#552E80',
  BROWN: '#6B3410',
  BLACK: '#000000',
}

export function BeltVisualizer({ belt, rank, className }: BeltVisualizerProps) {
  if (!belt) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-8',
          className
        )}
      >
        <div className="text-center">
          <svg
            className="mx-auto mb-2 h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
            />
          </svg>
          <p className="text-sm text-gray-600">No belt rank set</p>
        </div>
      </div>
    )
  }

  const backgroundColor = beltColors[belt]
  const borderColor = beltBorderColors[belt]
  const rankNumber = rank ? parseInt(rank, 10) : 0

  // Stripe colors - white for all belts (BJJ standard)
  const stripeColor = '#FFFFFF'
  const stripeBorderColor = 'rgba(0, 0, 0, 0.2)'

  return (
    <div className={cn('relative w-full max-w-sm', className)}>
      {/* Belt container - horizontal like a real belt */}
      <div
        className="relative h-24 w-full overflow-hidden rounded-lg shadow-lg"
        style={{
          backgroundColor,
          border: `3px solid ${borderColor}`,
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Stripes - extending full width like real belt ranking */}
        {rankNumber > 0 && (
          <div className="absolute inset-0 flex items-center justify-end gap-3 px-8">
            {Array.from({ length: rankNumber }).map((_, i) => (
              <div
                key={i}
                className="relative h-full w-5 shadow-md"
                style={{
                  backgroundColor: stripeColor,
                  border: `1px solid ${stripeBorderColor}`,
                  boxShadow:
                    '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.3), inset 0 -1px 2px rgba(0, 0, 0, 0.2)',
                }}
              >
                {/* Subtle tape texture */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    background:
                      'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0, 0, 0, 0.5) 2px, rgba(0, 0, 0, 0.5) 4px)',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* White belt text (for visibility when no stripes) */}
        {belt === 'WHITE' && rankNumber === 0 && (
          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-400">
            White Belt
          </span>
        )}

        {/* Belt texture overlay for realism */}
        <div
          className="pointer-events-none absolute inset-0 opacity-5"
          style={{
            background:
              'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 0, 0, 0.3) 2px, rgba(0, 0, 0, 0.3) 3px)',
          }}
        />
      </div>

      {/* Label below */}
      <div className="mt-3 text-center">
        <p className="text-sm font-semibold text-black">
          {belt.charAt(0) + belt.slice(1).toLowerCase()} Belt
        </p>
        {rankNumber > 0 && (
          <p className="text-xs text-gray-600">
            {rankNumber} {rankNumber === 1 ? 'Stripe' : 'Stripes'}
          </p>
        )}
      </div>
    </div>
  )
}
